import { DateTime } from "luxon";
import { OrganizationType } from "../../enums";
import { TicketingFormCategory } from "../../enums/ticketing";
import { getRegionShortCodeFromCountryAndRegionName } from "../../helpers/country";
import { CountryNames, OrganizationCountry } from "../enums/country";
import { formatCharityNumberForDisplay } from "./ein";
import { isDePostalCodeValid, isDeLegalFormEligibleForReceipt } from "./de-fiscal";
import { getPendingReceiptYearBoundaries, getPendingReceiptYearForDate } from "./dates";
export const ANNUAL_RECEIPT_ANNEX_COUNTRIES = [OrganizationCountry.Germany];
export const requiresAnnualReceiptAnnex = (country) => Boolean(country && ANNUAL_RECEIPT_ANNEX_COUNTRIES.includes(country));
export const getActualYearBoundary = ({ date, organizationCountry, timezone, }) => {
    if (organizationCountry === OrganizationCountry.Germany) {
        const localDate = DateTime.fromJSDate(date, { zone: timezone ?? undefined });
        if (!localDate.isValid) {
            return null;
        }
        return [localDate.startOf("year").toJSDate(), localDate.endOf("year").toJSDate()];
    }
    return getPendingReceiptYearBoundaries({
        year: getPendingReceiptYearForDate({ date, organizationCountry }),
        organizationCountry,
    });
};
const AUSTRALIAN_STATE_ABBREVIATIONS = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];
export const getAustralianStateAbbreviation = (region) => {
    if (!region) {
        return undefined;
    }
    const trimmed = region.trim().toUpperCase();
    if (AUSTRALIAN_STATE_ABBREVIATIONS.includes(trimmed)) {
        return trimmed;
    }
    return getRegionShortCodeFromCountryAndRegionName(CountryNames.Australia, region.trim()) ?? region;
};
export const hasOrganizationAdministrativeInformation = ({ address, city, postalCode, region, }) => {
    return Boolean(address && city && postalCode && region);
};
export const isAddressFull = (country, command) => {
    if (country === OrganizationCountry.UnitedStates) {
        return Boolean(command?.country);
    }
    return Boolean(command?.address && command?.postalCode && command.city && command.country);
};
export const blockReceiptWithoutCityOrPostal = (country) => {
    return (country === OrganizationCountry.Canada ||
        country === OrganizationCountry.Australia ||
        country === OrganizationCountry.Germany);
};
export const RECEIPT_TABLE_CONSTRAINTS = {
    CITY_NOT_NULL: `organization_country <> '${OrganizationCountry.Canada}' OR city IS NOT NULL`,
    POSTAL_CODE_NOT_NULL: `organization_country <> '${OrganizationCountry.Canada}' OR postal_code IS NOT NULL`,
    AUSTRALIA_CITY_NOT_NULL: `organization_country <> '${OrganizationCountry.Australia}' OR city IS NOT NULL`,
    AUSTRALIA_POSTAL_CODE_NOT_NULL: `organization_country <> '${OrganizationCountry.Australia}' OR postal_code IS NOT NULL`,
};
export const DEFAULT_ORGANIZATION_RECEIPT_COUNTRY = OrganizationCountry.Canada;
export const isReceiptAllowFormCountry = (country) => {
    return Boolean(country && country !== OrganizationCountry.GreatBritain && country !== OrganizationCountry.Ireland);
};
export const getFakeReceiptAddress = (country) => {
    if (country === OrganizationCountry.Canada) {
        return "Nonprofit's full address / Adresse de l'organisme";
    }
    return "Nonprofit's full address";
};
export const getFakeReceiptCity = (country) => {
    if (country === OrganizationCountry.Canada) {
        return "Montréal";
    }
    if (country === OrganizationCountry.Australia) {
        return "Sydney";
    }
    if (country === OrganizationCountry.GreatBritain) {
        return "London";
    }
    if (country === OrganizationCountry.Germany) {
        return "Berlin";
    }
    return "New York";
};
export const getFakeReceiptRegion = (country) => {
    if (country === OrganizationCountry.Canada) {
        return "Québec";
    }
    if (country === OrganizationCountry.Australia) {
        return "NSW";
    }
    if (country === OrganizationCountry.GreatBritain) {
        return "London";
    }
    if (country === OrganizationCountry.Germany) {
        return "Berlin";
    }
    return "New York";
};
export const canOrganizationGenerateReceipt = ({ country, hasAdministrativeInformation, hasCharityNumber, hasDeReceiptFields, deLegalForm, signatureUrl, type, }) => {
    if (type !== OrganizationType.Charity && type !== OrganizationType.Other501C) {
        return false;
    }
    if (country === OrganizationCountry.GreatBritain || country === OrganizationCountry.Ireland) {
        return false;
    }
    else if (country === OrganizationCountry.Canada) {
        return hasAdministrativeInformation && hasCharityNumber && Boolean(signatureUrl);
    }
    else if (country === OrganizationCountry.UnitedStates) {
        return hasAdministrativeInformation && hasCharityNumber;
    }
    else if (country === OrganizationCountry.Australia) {
        return hasAdministrativeInformation && hasCharityNumber;
    }
    else if (country === OrganizationCountry.Germany) {
        return (hasAdministrativeInformation &&
            hasCharityNumber &&
            hasDeReceiptFields &&
            isDeLegalFormEligibleForReceipt(deLegalForm) &&
            Boolean(signatureUrl));
    }
    return false;
};
export const isSignatureRequiredForGenerateReceipt = (country) => {
    return country === OrganizationCountry.Canada || country === OrganizationCountry.Germany;
};
export const isDonorAddressRequiredForReceipt = (country) => {
    return (country === OrganizationCountry.Canada ||
        country === OrganizationCountry.Australia ||
        country === OrganizationCountry.Germany);
};
export const getReceiptTemplatePath = (country, isAnnualReceipt = false) => {
    if (country === OrganizationCountry.Australia) {
        return "receipt/australiaV2.hbs";
    }
    if (country === OrganizationCountry.Canada) {
        return "receipt/canadaV2.hbs";
    }
    if (country === OrganizationCountry.UnitedStates) {
        return "receipt/unitedstatesV2.hbs";
    }
    if (country === OrganizationCountry.Germany) {
        return isAnnualReceipt ? "receipt/germanyAnnualV2.hbs" : "receipt/germanyV2.hbs";
    }
    throw new Error(`Receipt template path not found for country: ${country}`);
};
export const isReceiptPostalCodeValid = (country, postalCode) => {
    if (country !== OrganizationCountry.Germany) {
        return true;
    }
    return isDePostalCodeValid(postalCode);
};
export const getReceiptPaperFormat = (country) => {
    return country === OrganizationCountry.Germany ? "a4" : "letter";
};
export const getReceiptNumber = (country, { organizationNumber, sequentialNumber, charityNumber, }) => {
    // Australia format: [ABN_LAST4]-[YYYY]-[SEQUENTIAL_7]
    if (country === OrganizationCountry.Australia) {
        const abnLast4 = charityNumber?.slice(-4) || "0000";
        const currentYear = new Date().getFullYear();
        return `${abnLast4}-${currentYear}-${sequentialNumber}`;
    }
    return "SI2-" + organizationNumber + "-" + sequentialNumber + "";
};
export const shouldDisplayCharityNumberOnTransactionReceipt = ({ charityNumber, deTaxNumber, hasGiftAidConfirmation, organizationCountry, }) => {
    if (organizationCountry === OrganizationCountry.Germany) {
        return Boolean(deTaxNumber);
    }
    return Boolean(charityNumber && (hasGiftAidConfirmation || organizationCountry === OrganizationCountry.Australia));
};
export const getTransactionReceiptCharityNumberLabelKey = ({ organizationCountry, }) => {
    if (organizationCountry === OrganizationCountry.Australia) {
        return "notification.transactionReceipt.charityNumberLabel.australia";
    }
    if (organizationCountry === OrganizationCountry.Germany) {
        return "notification.transactionReceipt.charityNumberLabel.germany";
    }
    return "notification.transactionReceipt.charityNumberLabel.default";
};
export const getTransactionReceiptCharityNumber = ({ charityNumber, deTaxNumber, organizationCountry, }) => {
    if (organizationCountry === OrganizationCountry.Germany) {
        return deTaxNumber ?? undefined;
    }
    return formatCharityNumberForDisplay(organizationCountry, charityNumber) ?? undefined;
};
export const donationFormCanGenerateReceipt = (donationForm, organization) => organization.canGenerateReceipt && donationForm.hasReceipt;
export const canFormCategoryGenerateReceipt = (country, formCategory) => !(country === OrganizationCountry.Germany && formCategory === TicketingFormCategory.MembershipV2);
export const ticketingCanGenerateReceipt = (ticketing, rates, organization) => {
    if (!canFormCategoryGenerateReceipt(organization.country, ticketing.formCategory)) {
        return false;
    }
    const formCanGenerateReceipt = (ticketing.extraDonation && ticketing.generateExtraDonationReceipt) ||
        rates.some((rate) => Boolean(rate.eligibleAmount));
    return organization.canGenerateReceipt && formCanGenerateReceipt;
};
